import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import categoryService from "../../services/category";
import eventService from "../../services/event";
import voucherService from "../../services/voucher";
import queryDefault from "../../utils/queryDefault";
import cookie from "../../utils/useCookie";
import LoadingPage from "../LoadingPage";

export default function VoucherCreate() {
  const { id } = useParams();
  const notification = useNotification();
  const navigate = useNavigate();
  const user = JSON.parse(cookie.getCookie("USER") || "");
  const [isLoading, setIsLoading] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);

  const form = useForm({
    defaultValues: {
      event_id: "",
      code: "",
      available: 0,
      is_persen: false,
      max_amount: 0,
      amount: 0,
      expired_at: "",
      is_expired: false,
      username: user?.username,
      id: id,
      category_exceptions: [],
    },
  });

  const listEvent = useQuery(
    ["events"],
    () => {
      return eventService.getAllEvent({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  const updateVoucher = useMutation(
    "update_voucher",
    voucherService.updateVoucher,
    {
      onSuccess: () => {
        navigate("/admin/voucher/");

        notification.onOpen({
          message: "Voucher was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const createVoucher = useMutation(
    "create_voucher",
    voucherService.createVoucher,
    {
      onSuccess: () => {
        navigate("/admin/voucher/");

        notification.onOpen({
          message: "Voucher was successfully created!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    if (id) {
      return updateVoucher.mutate(values);
    } else {
      createVoucher.mutate(values);
    }
  };

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      voucherService
        .getVoucherById(id)
        .then(({ data }) => {
          form.setValue("code", get(data, "code"));
          form.setValue("event_id", get(data, "event_id"));
          form.setValue("amount", get(data, "amount"));
          form.setValue("available", get(data, "available"));
          form.setValue("max_amount", get(data, "max_amount"));
          form.setValue("is_persen", get(data, "is_persen"));
          form.setValue("expired_at", get(data, "expired_at"));
          form.setValue("is_expired", get(data, "is_expired"));
          form.setValue("category_exceptions", get(data, "categoryException").map((item: any) => item.category_id));
          setSelectedCategories(form.getValues("category_exceptions"));
          categoryService.getCategoryListByEventId({ event_id: get(data, "event_id") }).then(response => {
            setCategories(response.data);
            setShowCategories(true);
          }).catch(error => {
            setCategories([])
            console.log(error)
          });
        })
        .catch(() => {
          notification.onOpen({
            message: "Something went wrong",
            type: "error",
            position: "top",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  const onSelectionChange = (event: any) => {
    const value = event.target.value;
    form.setValue("category_exceptions", value);
    setSelectedCategories(value);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label={"Create Voucher"}
        breadcrumbs={[{ label: "Voucher", href: "/admin/voucher" }]}
        href={"/admin/voucher"}
      />
      {isLoading ? (
        <LoadingPage />
      ) : (
        <ProtectedPermissionPage acceptPermissions={[1, 3]}>
          <Box sx={{ mt: 2 }}>
            <Grid container>
              <Grid item lg={4} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Event</FormLabel>
                  <Controller
                    name="event_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                          onChange={event => {
                            form.setValue("event_id", event.target.value);
                            categoryService.getCategoryListByEventId({ event_id: Number(event.target.value) }).then(response => {
                              setCategories(response.data);
                              setShowCategories(true);
                            }).catch(error => {
                              setCategories([])
                              console.log(error)
                            })
                          }}
                          disabled={!!id}
                        >
                          {get(listEvent, ["data", "data"], []).map(
                            (item: any, i: any) => {
                              return (
                                <MenuItem value={item?.id} key={item?.id}>
                                  {item?.name}
                                </MenuItem>
                              );
                            }
                          )}
                        </Select>

                        <FormHelperText
                          error={Boolean(fieldState.error?.message)}
                        >
                          {fieldState.error?.message}
                        </FormHelperText>
                      </Box>
                    )}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Category Exceptions</FormLabel>
                  <Controller
                    name="category_exceptions"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          multiple
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                          renderValue={(selected: any) => selected.map((id: any) => {
                            var category = ""
                            categories.forEach((element: any) => {
                              if (element.id === id) {
                                category = element.name
                              }
                            });
                            return category;
                          }).join(", ")}
                          onChange={onSelectionChange}
                          disabled={!showCategories}
                        >
                          {categories.map((category: any) => (
                            <MenuItem value={category.id} key={category.id}>
                              <ListItemText primary={category.name} />
                              <Checkbox checked={selectedCategories.indexOf(category.id) > -1} />
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Code</FormLabel>
                  <Controller
                    name="code"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        disabled={!!id}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Total Voucher</FormLabel>
                  <Controller
                    name="available"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="number"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
                <FormControl sx={{ mt: 2 }}>
                  <FormLabel required>Amount</FormLabel>
                  <Controller
                    name="amount"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="number"
                        inputRef={ref}
                        disabled={!!id}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
                <FormControl sx={{ mt: 5 }}>
                  <FormControlLabel
                    label="Is Persen"
                    labelPlacement="start"
                    control={
                      <Controller
                        name="is_persen"
                        control={form.control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            disabled={!!id}
                            checked={form.getValues().is_persen}
                          />
                        )}
                      />
                    }
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Max Amount</FormLabel>
                  <Controller
                    name="max_amount"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="number"
                        inputRef={ref}
                        disabled={!!id}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Expiration Date</FormLabel>
                  <Controller
                    name="expired_at"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="datetime-local"
                        inputRef={ref}
                        disabled={!!id}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </FormControl>
                <FormControl sx={{ mt: 2 }}>
                  <FormControlLabel
                    label="Is Expired"
                    labelPlacement="start"
                    control={
                      <Controller
                        name="is_expired"
                        control={form.control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            disabled={!!id}
                            checked={form.getValues().is_expired}
                          />
                        )}
                      />
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Stack direction="row" spacing={1}>
              <LoadingButton
                variant="contained"
                onClick={form.handleSubmit(onSubmit)}
                loading={createVoucher.isLoading}
              >
                SAVE
              </LoadingButton>

              <Button variant="outlined" onClick={() => navigate(-1)}>
                CANCEL
              </Button>
            </Stack>
          </Box>
        </ProtectedPermissionPage>
      )}
    </MainLayout>
  );
}
